import { filter, reduce } from 'lodash';

const getTextWidth = (text: string, font = 'bold 12px sans-serif') => {
  const canvas = document.createElement('canvas');
  const context: any = canvas.getContext('2d');
  context.font = font;
  return context.measureText(text).width;
};

const AVERAGE_SYMBOL_WIDTH = 11;

function getColumnText({ items, clientWidth, maxVisibleItems = 3 }): string {
  if (items.length > 1) {
    const itemsWithoutEmpty = filter(items, item => !!item);
    const maxSuffix = 'and 9999 more';

    const { value } = reduce(
      itemsWithoutEmpty,
      (acc, item, index: number) => {
        const width = getTextWidth(`${item}, ${maxSuffix}`);

        if (acc.length < maxVisibleItems && clientWidth > width + acc.width) {
          if (index) {
            acc.value += ', ';
          }

          acc.width += width;
          acc.value = `${acc.value}${item}`;
          acc.remaining -= 1;
          acc.length += 1;
        } else if (!acc.value) {
          const deltaWithSymbols = Math.floor((width - clientWidth) / AVERAGE_SYMBOL_WIDTH);

          // if first item is bigger than container we should cut it and add `...` ( - 3)
          acc.value = `${item.substr(0, item.length - deltaWithSymbols - 3)}...,`;
          acc.width += width;
          acc.remaining -= 1;
        }

        if (index === itemsWithoutEmpty.length - 1 && acc.remaining) {
          const suffix = ` and ${acc.remaining} more`;
          acc.value += suffix;
        }

        return acc;
      },
      { width: 0, value: '', remaining: itemsWithoutEmpty.length, length: 0 }
    );

    return value;
  }
  return items;
}

const breakString = (word: string, maxWidth: number, font: string, hyphenCharacter = ' ') => {
  const characters = word.split('');
  const lines: string[] = [];
  let currentLine = '';
  characters.forEach((character, index) => {
    const nextLine = `${currentLine}${character}`;
    const lineWidth = getTextWidth(nextLine, font);
    if (lineWidth >= maxWidth) {
      const currentCharacter = index + 1;
      const isLastLine = characters.length === currentCharacter;
      const hyphenatedNextLine = `${nextLine}${hyphenCharacter}`;
      lines.push(isLastLine ? nextLine : hyphenatedNextLine);
      currentLine = '';
    } else {
      currentLine = nextLine;
    }
  });
  return { hyphenatedStrings: lines, remainingWord: currentLine };
};

function wrapLabel(label: string = '', maxWidth: number, font: string) {
  const words = label.split(' ');
  const completedLines: string[] = [];
  let nextLine = '';
  words.forEach((word, index) => {
    const wordLength = getTextWidth(`${word} `);
    const nextLineLength = getTextWidth(nextLine);
    if (wordLength > maxWidth) {
      const { hyphenatedStrings, remainingWord } = breakString(word, maxWidth, font);
      completedLines.push(nextLine, ...hyphenatedStrings);
      nextLine = remainingWord;
    } else if (nextLineLength + wordLength >= maxWidth) {
      completedLines.push(nextLine);
      nextLine = word;
    } else {
      nextLine = [nextLine, word].filter(Boolean).join(' ');
    }
    const currentWord = index + 1;
    const isLastWord = currentWord === words.length;
    if (isLastWord) {
      completedLines.push(nextLine);
    }
  });
  return completedLines.filter(line => line !== '');
}

export { getColumnText, wrapLabel };
